<script>
/* global google */
import appConfig from '../../../../../shared/helpers/appConfig';
import { mapActions, mapState } from 'vuex';

export default {
  name: "GoogleDrivePicker",
  data() {
    return {
      pickerApiLoaded: false,
      oauthToken: null,
      isLoading: false,
      pickerInitializing: false,
      pickerInitialized: false,
      errorMessage: null,
      validMimeTypes: [
        'text/plain',
        'text/markdown',
        'application/pdf',
        'text/html',
        'text/xml',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'text/csv',
        'application/vnd.ms-powerpoint',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'application/xml',
        'application/epub+zip',
        'message/rfc822',
        'application/vnd.ms-outlook'
      ],
    };
  },
  computed: {
    ...mapState('knowledge', ['uploadedFiles', 'isUploaded']),
    googleClientId() {
      return appConfig('VUE_APP_GOOGLE_CLIENT_ID');
    },
    googleApiKey() {
      return appConfig('VUE_APP_GOOGLE_API_KEY');
    },
    googlePickerApiLoaded() {
      return !!window.google?.picker;
    },
    googleGapiLoaded() {
      return !!window.gapi;
    },
    googleOAuthApiLoaded() {
      return !!window.google?.accounts?.oauth2;
    },
    uploadedFilesCount() {
      return this.uploadedFiles ? this.uploadedFiles.length : 0;
    },
    totalFileSize() {
      if (!this.uploadedFiles) return 0;
      return this.uploadedFiles.reduce((total, file) => total + file.size, 0);
    }
  },
  methods: {
    ...mapActions('knowledge', ['handleFilesUpload', 'handleRemoveFile', 'removeUploadedFileAt', 'updateIsUploaded']),
    
    // Load Google API client libraries
    loadGoogleApis() {
      this.pickerInitializing = true;

      // First, load the Google API client
      if (!this.googleGapiLoaded) {
        const script = document.createElement('script');
        script.src = 'https://apis.google.com/js/api.js';
        script.onload = () => {
          this.onGapiLoaded();
        };
        document.body.appendChild(script);
      } else {
        this.onGapiLoaded();
      }
    },

    // After GAPI is loaded, load the OAuth2 library
    onGapiLoaded() {
      if (!this.googleOAuthApiLoaded) {
        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.onload = () => {
          this.onOAuthLoaded();
        };
        document.body.appendChild(script);
      } else {
        this.onOAuthLoaded();
      }
    },

    // After OAuth is loaded, authenticate the user
    onOAuthLoaded() {
      window.gapi.load('picker', this.onPickerApiLoad);
      this.initializeAuth();
    },

    // Initialize the OAuth client
    initializeAuth() {
      const client = google.accounts.oauth2.initTokenClient({
        client_id: this.googleClientId,
        scope: 'https://www.googleapis.com/auth/drive.readonly',
        callback: (response) => {
          if (response.error) {
            this.errorMessage = `Authentication error: ${response.error}`;
            this.pickerInitializing = false;
            return;
          }
          
          this.oauthToken = response.access_token;
          if (this.pickerApiLoaded) {
            this.createPicker();
          }
        },
      });

      client.requestAccessToken();
    },

    // After Picker API is loaded
    onPickerApiLoad() {
      this.pickerApiLoaded = true;
      if (this.oauthToken) {
        this.createPicker();
      }
    },

    // Create and render the Google Drive Picker
    createPicker() {
      if (!this.pickerApiLoaded || !this.oauthToken) {
        return;
      }

      this.pickerInitializing = false;
      this.pickerInitialized = true;

      // Set up view with only supported file types
      const view = new google.picker.DocsView(google.picker.ViewId.DOCS);
      view.setMimeTypes(this.validMimeTypes.join(','));
      
      const picker = new google.picker.PickerBuilder()
        .enableFeature(google.picker.Feature.NAV_HIDDEN)
        .enableFeature(google.picker.Feature.MULTISELECT_ENABLED)
        .setAppId(this.googleClientId.split('-')[0])
        .setOAuthToken(this.oauthToken)
        .setDeveloperKey(this.googleApiKey)
        .setCallback(this.pickerCallback)
        .addView(view)
        .setTitle(this.$t('KNOWLEDGE.GOOGLEDRIVEPICKER.SELECT_FILES_BUTTON'))
        .build();
      
      picker.setVisible(true);
    },

    // Handle user's file selection from the Picker
    async pickerCallback(data) {
      if (data.action === google.picker.Action.PICKED) {
        this.isLoading = true;
        const docs = data.docs;

        try {
          // Process the selected files
          const files = await Promise.all(docs.map(doc => this.processGoogleDriveFile(doc)));
          
          // Filter out any null results (failed downloads)
          const validFiles = files.filter(file => file !== null);
          
          if (validFiles.length > 0) {
            // Update files in store
            this.handleFilesUpload(validFiles);
            this.updateIsUploaded(true);
          } else {
            this.errorMessage = 'No valid files could be processed.';
          }
        } catch (error) {
          console.error('Error processing files:', error);
          this.errorMessage = `Error processing files: ${error.message || 'Unknown error'}`;
        } finally {
          this.isLoading = false;
        }
      } else if (data.action === google.picker.Action.CANCEL) {
        console.log('User cancelled picker selection');
      }
    },

    // Download and process a file from Google Drive
    async processGoogleDriveFile(doc) {
      try {
        // Check if the file type is supported
        if (!this.validMimeTypes.includes(doc.mimeType)) {
          console.warn(`Skipping unsupported file type: ${doc.mimeType}`);
          return null;
        }

        // Download the file from Google Drive
        const response = await fetch(`https://www.googleapis.com/drive/v3/files/${doc.id}?alt=media`, {
          headers: {
            'Authorization': `Bearer ${this.oauthToken}`
          }
        });

        if (!response.ok) {
          throw new Error(`Failed to download file: ${response.statusText}`);
        }

        // Get the file content as blob
        const blob = await response.blob();
        
        // Create a File object from the blob
        const file = new File([blob], doc.name, { 
          type: doc.mimeType,
          lastModified: new Date().getTime()
        });

        return file;
      } catch (error) {
        console.error(`Error processing file ${doc.name}:`, error);
        return null;
      }
    },

    // Trigger the Google Picker
    openPicker() {
      this.errorMessage = null;
      if (!this.pickerInitialized) {
        this.loadGoogleApis();
      } else {
        if (this.oauthToken && this.pickerApiLoaded) {
          this.createPicker();
        } else {
          this.loadGoogleApis();
        }
      }
    }
  }
};
</script>

<template>
  <div>
    <div class="bg-slate-200 dark:bg-slate-800 p-6 rounded-md max-w-xl">
      <p class="mb-4">{{ $t('KNOWLEDGE.GOOGLEDRIVEPICKER.SELECT_FILES_INFO') }}</p>

      <button
        @click="openPicker"
        class="bg-woot-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 flex items-center justify-center gap-2"
        :disabled="pickerInitializing || isLoading"
      >
        <span v-if="pickerInitializing || isLoading">
          <svg class="animate-spin h-5 w-5 text-white" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            ></circle>
            <circle
              class="opacity-75"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
              stroke-dasharray="80"
              stroke-dashoffset="60"
            ></circle>
          </svg>
        </span>
        <span v-else>
          <fluent-icon icon="arrow-download" class="h-5 w-5" />
        </span>
        {{ pickerInitializing ? $t('KNOWLEDGE.GOOGLEDRIVEPICKER.INITIALIZING') : isLoading ? $t('KNOWLEDGE.GOOGLEDRIVEPICKER.PROCESSING') : $t('KNOWLEDGE.GOOGLEDRIVEPICKER.SELECT_FILES_BUTTON') }}
      </button>

      <p class="text-xs text-slate-500 dark:text-slate-300 mt-2">
        {{ $t('KNOWLEDGE.GOOGLEDRIVEPICKER.SUPPORTED_FORMATS') }}
      </p>

      <div v-if="errorMessage" class="mt-4 text-red-500 p-2 bg-red-100 dark:bg-red-900 rounded-md">{{ errorMessage }}</div>
    </div>

    <div v-if="isUploaded" class="mt-4">
      <div v-if="uploadedFilesCount === 1" class="border-2 bg-slate-200 dark:bg-slate-800 flex rounded-md px-5 py-2 justify-between max-w-xl items-center">
        <fluent-icon icon="arrow-download" class="h-[30px] w-[30px]"/>
        <span>
          <h1>{{ uploadedFiles[0].name }}</h1>
          <p class="font-light">{{ (uploadedFiles[0].size / 1000000).toFixed(2) }} {{ $t('KNOWLEDGE.GOOGLEDRIVEPICKER.MB_UNIT') }}</p>
        </span>
        <button :aria-label="$t('KNOWLEDGE.GOOGLEDRIVEPICKER.REMOVE_FILE')" class="hover:bg-slate-300 dark:hover:bg-slate-700 p-2 rounded-full" @click="handleRemoveFile">
          <fluent-icon icon="delete" />
        </button>
      </div>

      <div v-else class="border-2 bg-slate-200 dark:bg-slate-800 rounded-md px-5 py-3 max-w-xl">
        <div class="flex justify-between items-center mb-3">
          <div>
            <h1 class="text-lg font-medium">{{ uploadedFilesCount }} {{ $t('KNOWLEDGE.GOOGLEDRIVEPICKER.FILES_SELECTED') }}</h1>
            <p class="font-light">{{ $t('KNOWLEDGE.GOOGLEDRIVEPICKER.TOTAL_SIZE') }} {{ (totalFileSize / 1000000).toFixed(2) }} {{ $t('KNOWLEDGE.GOOGLEDRIVEPICKER.MB_UNIT') }}</p>
          </div>
          <button :aria-label="$t('KNOWLEDGE.GOOGLEDRIVEPICKER.REMOVE_ALL_FILES')" class="hover:bg-slate-300 dark:hover:bg-slate-700 p-2 rounded-full" @click="handleRemoveFile">
            <fluent-icon icon="delete" />
          </button>
        </div>

        <div class="max-h-40 overflow-y-auto">
          <div v-for="(file, index) in uploadedFiles" :key="index" class="flex items-center py-1 border-t border-slate-300 dark:border-slate-700">
            <fluent-icon icon="document" class="h-[18px] w-[18px] mr-2"/>
            <span class="flex-1 truncate">{{ file.name }}</span>
            <span class="text-sm text-slate-500 mr-2">{{ (file.size / 1000000).toFixed(2) }} {{ $t('KNOWLEDGE.GOOGLEDRIVEPICKER.MB_UNIT') }}</span>
            <button :aria-label="$t('KNOWLEDGE.GOOGLEDRIVEPICKER.REMOVE_FILE')" class="hover:bg-slate-300 dark:hover:bg-slate-700 p-1 rounded-full" @click="removeUploadedFileAt(index)">
              <fluent-icon icon="dismiss" class="h-[16px] w-[16px]" />
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
  /* Any specific styles for the component */
</style>
